import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';

import useResizeObserver, { ObservedSize } from 'use-resize-observer';

import { XYPosition } from '../../hooks/use-dynamic-viewport';

import styles from './index.module.scss';

export interface CoreLaneTooltipProps {
	x: number;
	y: number;
	viewPortWidth: number;
	viewPortHeight: number;
}

const CoreLaneTooltip: React.FC<PropsWithChildren<CoreLaneTooltipProps>> = ({
	x,
	y,
	viewPortWidth,
	viewPortHeight,
	children,
}) => {
	const [position, setPosition] = useState<XYPosition>();
	const [size, setSize] = useState<ObservedSize>();

	const onResize = (size: ObservedSize): void => {
		setSize(size);
	};

	useEffect(() => {
		if (!size) return;
		const { width, height } = size;
		if (!width || !height) {
			setPosition(undefined);
			return;
		}
		// Distance to place the tooltip away from the cursor
		const offset = 16;
		setPosition({
			// Must be positive number for Firefox
			x:
				x + width + 16 - offset > viewPortWidth
					? // Then shift it to the left to fit
						viewPortWidth - width - 16 - offset
					: // Otherwise, put it where it should go
						x + offset - 16,
			y:
				y < height + offset || (x + width > viewPortWidth && y + height < viewPortHeight)
					? y + offset
					: y - height - offset,
		});
	}, [size, viewPortHeight, viewPortWidth, x, y]);

	const ref = useRef<HTMLDivElement>(null);
	useResizeObserver({ ref, onResize });

	// If the tooltip is larger than the remaining horizontal space in the window,
	// then make it fit without going outside the edges. 32px is the buffer on the edges
	return (
		<foreignObject
			className="core-lane-tooltip"
			// Place offscreen while waiting for childrenWidth to stop flicker
			data-tooltip-visible={position ? true : undefined}
			{...position}
			style={{
				padding: 4,
				zIndex: 1,
				overflow: 'visible',
				width: 1,
				height: 1,
				visibility: position ? 'visible' : 'hidden',
			}}
		>
			<div style={{ display: 'inline-block' }} className={styles.tooltip} ref={ref}>
				{children}
			</div>
		</foreignObject>
	);
};

export default CoreLaneTooltip;
